/**
 * Subtasks (US 4.5), attachments (US 4.6), comments with @mentions (US 4.7)
 * and the activity log (US 4.8) — the sections of a task's detail.
 */
import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { type LucideIcon, Archive, ArchiveRestore, Bell, CalendarDays, Check, ChevronLeft, CircleCheck, CircleDot, CirclePlus, Download, FileText, Flag, FolderInput, ListChecks, ListPlus, MoreHorizontal, Paperclip, Pencil, Plus, Repeat, Reply, RotateCcw, Send, Tag, Trash2, UserPlus, Users, X } from "lucide-react";
import { cn } from "@weco/ui";
import { ConfirmDialog, Dropdown } from "../ui";
import { PersonAvatar } from "../hubs/parts";
import type { HubPerson } from "../hubs/api";
import {
  attachmentBlobUrl,
  taskApi,
  uploadAttachment,
  useActivity,
  useComments,
  type ActivityRow,
  type Attachment,
  type Comment,
  type TaskApiError,
  type TaskFull,
} from "./api";
import { Markdown } from "./fields";
import { ThreadSummary } from "../mochi/ThreadSummary";

const MAX_MB = 20;

export function SectionTitle({ icon: Icon, children, extra }: { icon: typeof ListChecks; children: React.ReactNode; extra?: React.ReactNode }) {
  return (
    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-3">
      <Icon className="size-3" /> {children}
      {extra}
    </p>
  );
}

// ── Subtasks (US 4.5) ─────────────────────────────────────────────────────

export function SubtasksSection({ task, onChanged }: { task: TaskFull; onChanged: (t: TaskFull) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const done = task.subtasks.filter((s) => s.done).length;
  const total = task.subtasks.length;
  const run = async (fn: () => Promise<TaskFull>) => {
    setBusy(true);
    try {
      onChanged(await fn());
    } catch (err) {
      toast.error((err as Error).message || t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));
    } finally {
      setBusy(false);
    }
  };
  const add = () => {
    const title = draft.trim();
    if (!title || busy) return;
    setDraft("");
    void run(() => taskApi.addSubtask(task.id, title));
  };
  return (
    <section className="px-5 py-4 border-b border-border" aria-label={t("tasks.subtasks.title", { defaultValue: "Subtasks" })}>
      <SectionTitle
        icon={ListChecks}
        extra={total > 0 && <span className="font-normal normal-case text-muted-foreground/60 ms-1">{t("tasks.subtasks.count", { done, total, defaultValue: "{{done}}/{{total}}" })}</span>}
      >
        {t("tasks.subtasks.title", { defaultValue: "Subtasks" })}
      </SectionTitle>
      {total > 0 && (
        <div className="mb-3">
          <div className="w-full h-1 bg-muted rounded-full overflow-hidden mb-3" role="progressbar" aria-valuenow={done} aria-valuemax={total}>
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${(done / total) * 100}%` }} />
          </div>
          <ul className="space-y-1">
            {task.subtasks.map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-muted/30 transition-colors group/sub">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={s.done}
                  aria-label={s.title}
                  disabled={!task.can_edit}
                  onClick={() => void run(() => taskApi.updateSubtask(s.id, { done: !s.done }))}
                  className={cn(
                    "w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                    s.done ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary",
                  )}
                >
                  {s.done && <Check className="size-2.5" />}
                </button>
                <span className={cn("text-sm flex-1 min-w-0 break-words", s.done && "line-through text-muted-foreground")}>{s.title}</span>
                {task.can_edit && (
                  <button
                    type="button"
                    onClick={() => void run(() => taskApi.removeSubtask(s.id))}
                    aria-label={t("tasks.subtasks.remove", { defaultValue: "Remove {{name}}", name: s.title })}
                    className="opacity-0 group-hover/sub:opacity-100 focus-visible:opacity-100 text-muted-foreground hover:text-red-500 transition-opacity"
                  >
                    <X className="size-[13px]" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {task.can_edit && (
        <div className="flex items-center gap-2 mt-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            aria-label={t("tasks.subtasks.add", { defaultValue: "Add subtask" })}
            placeholder={t("tasks.subtasks.addPlaceholder", { defaultValue: "Add subtask…" })}
            className="flex-1 text-sm bg-muted/20 border border-border rounded-lg px-3 py-2 outline-none focus:border-primary transition-colors"
          />
          <button
            type="button"
            onClick={add}
            disabled={!draft.trim() || busy}
            aria-label={t("tasks.subtasks.add", { defaultValue: "Add subtask" })}
            className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-primary/90 transition-all"
          >
            {t("tasks.subtasks.addShort", { defaultValue: "Add" })}
          </button>
        </div>
      )}
    </section>
  );
}

// ── Attachments (US 4.6) ──────────────────────────────────────────────────

interface Upload {
  key: string;
  file: File;
  progress: number;
  error: string;
}

const humanSize = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

function AttachmentRow({ a, task, me, onRemoved }: { a: Attachment; task: TaskFull; me: string; onRemoved: () => void }) {
  const { t, i18n } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const isImage = a.content_type.startsWith("image/");
  const isPdf = a.content_type === "application/pdf";
  useEffect(() => {
    if (!isImage) return;
    let url = "";
    let alive = true;
    void attachmentBlobUrl(a.id)
      .then((u) => {
        url = u;
        if (alive) setPreview(u);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [a.id, isImage]);
  const open = async (download: boolean) => {
    try {
      const url = await attachmentBlobUrl(a.id, !download);
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = a.name;
        link.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error(t("tasks.files.openFailed", { defaultValue: "Couldn't open the file." }));
    }
  };
  const canDelete = a.uploaded_by_email === me || task.can_admin;
  return (
    <li className="flex items-center gap-3 p-2 rounded-xl border border-border group/file">
      <button
        type="button"
        onClick={() => void open(false)}
        className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden"
        aria-label={isImage || isPdf ? t("tasks.files.preview", { defaultValue: "Preview {{name}}", name: a.name }) : a.name}
      >
        {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <FileText className="size-4 text-muted-foreground" />}
      </button>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{a.name}</span>
        <span className="block text-[11px] text-muted-foreground truncate">
          {humanSize(a.size)} · {a.uploaded_by} · {new Date(a.created_at).toLocaleDateString(i18n.language, { day: "numeric", month: "short" })}
        </span>
      </span>
      <button
        type="button"
        onClick={() => void open(true)}
        aria-label={t("tasks.files.download", { defaultValue: "Download {{name}}", name: a.name })}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <Download className="size-4" />
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          aria-label={t("tasks.files.delete", { defaultValue: "Delete {{name}}", name: a.name })}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover/file:opacity-100 focus-visible:opacity-100"
        >
          <Trash2 className="size-4" />
        </button>
      )}
      {confirm && (
        <ConfirmDialog
          title={t("tasks.files.deleteTitle", { defaultValue: "Delete {{name}}?", name: a.name })}
          body={t("tasks.files.deleteBody", { defaultValue: "The file is removed from this task for everyone." })}
          confirmLabel={t("tasks.files.deleteConfirm", { defaultValue: "Delete file" })}
          onClose={() => setConfirm(false)}
          onConfirm={() =>
            void taskApi
              .removeAttachment(a.id)
              .then(onRemoved)
              .catch(() => toast.error(t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." })))
          }
        />
      )}
    </li>
  );
}

export function AttachmentsSection({ task, me, onChanged }: { task: TaskFull; me: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const start = (file: File) => {
    const key = `${file.name}-${file.size}-${Math.random()}`;
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploads((u) => [...u, { key, file, progress: 0, error: t("tasks.files.tooLarge", { defaultValue: "Files can be up to {{mb}} MB.", mb: MAX_MB }) }]);
      return;
    }
    setUploads((u) => [...u, { key, file, progress: 0, error: "" }]);
    void uploadAttachment(task.id, file, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))))
      .then(() => {
        setUploads((u) => u.filter((x) => x.key !== key));
        onChanged();
      })
      .catch((err: Error) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: err.message } : x))));
  };
  const retry = (up: Upload) => {
    setUploads((u) => u.filter((x) => x.key !== up.key));
    start(up.file);
  };
  const takeFiles = (files: FileList | File[]) => Array.from(files).forEach(start);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (task.can_edit && e.dataTransfer.files?.length) takeFiles(e.dataTransfer.files);
  };
  const onPaste = (e: ClipboardEvent) => {
    if (!task.can_edit) return;
    const images = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith("image/"));
    if (images.length) {
      e.preventDefault();
      takeFiles(images);
    }
  };
  return (
    <section
      className={cn("px-5 py-4 border-b border-border transition-colors", over && "bg-primary/[0.05]")}
      aria-label={t("tasks.files.title", { defaultValue: "Attachments" })}
      onDragOver={(e) => {
        if (!task.can_edit) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onPaste={onPaste}
      tabIndex={-1}
    >
      <SectionTitle
        icon={Paperclip}
        extra={task.attachments.length > 0 && <span className="font-normal normal-case text-muted-foreground/70 ms-1">{task.attachments.length}</span>}
      >
        {t("tasks.files.title", { defaultValue: "Attachments" })}
      </SectionTitle>
      {(task.attachments.length > 0 || uploads.length > 0) && (
        <ul className="space-y-1.5 mb-2">
          {task.attachments.map((a) => (
            <AttachmentRow key={a.id} a={a} task={task} me={me} onRemoved={onChanged} />
          ))}
          {uploads.map((up) => (
            <li key={up.key} className={cn("flex items-center gap-3 p-2 rounded-xl border", up.error ? "border-red-500/40 bg-red-500/[0.04]" : "border-border")}>
              <FileText className="size-4 text-muted-foreground shrink-0 ms-3" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{up.file.name}</span>
                {up.error ? (
                  <span role="alert" className="block text-[11px] text-red-500">
                    {up.error}
                  </span>
                ) : (
                  <span className="block h-1 mt-1 bg-muted rounded-full overflow-hidden" role="progressbar" aria-valuenow={Math.round(up.progress * 100)}>
                    <span className="block h-full bg-primary transition-all" style={{ width: `${Math.round(up.progress * 100)}%` }} />
                  </span>
                )}
              </span>
              {up.error && (
                <>
                  <button type="button" onClick={() => retry(up)} className="h-7 px-2 rounded-md text-xs font-semibold hover:bg-muted flex items-center gap-1">
                    <RotateCcw className="size-3" /> {t("tasks.files.retry", { defaultValue: "Retry" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploads((u) => u.filter((x) => x.key !== up.key))}
                    aria-label={t("tasks.hubs.close", { defaultValue: "Close" })}
                    className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center"
                  >
                    <X className="size-3.5" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {task.can_edit && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="h-8 px-3 rounded-lg border border-dashed border-border text-xs font-semibold hover:bg-muted flex items-center gap-1.5"
          >
            <Plus className="size-3.5" /> {t("tasks.files.add", { defaultValue: "Add file" })}
          </button>
          <span className="text-[11px] text-muted-foreground">{t("tasks.files.limit", { defaultValue: "Drop or paste files · up to {{mb}} MB each", mb: MAX_MB })}</span>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) takeFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      )}
    </section>
  );
}

// ── Comments & activity (US 4.7, 4.8) ─────────────────────────────────────

/** "@Sa…" opens a picker of people who can open the task (US 4.7). */
function Composer({
  taskId,
  people,
  placeholder,
  initial = "",
  autoFocus,
  onSubmit,
  onCancel,
}: {
  taskId: string;
  people: HubPerson[];
  placeholder: string;
  initial?: string;
  autoFocus?: boolean;
  onSubmit: (body: string, mentions: string[]) => Promise<boolean>;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initial);
  const [mentions, setMentions] = useState<HubPerson[]>([]);
  const [query, setQuery] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const matches = query === null ? [] : people.filter((p) => !query || p.name.toLowerCase().includes(query) || p.email.includes(query)).slice(0, 6);
  const onChange = (value: string) => {
    setBody(value);
    const caret = ref.current?.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const at = /(^|\s)@([^\s@]*)$/.exec(before);
    setQuery(at ? at[2].toLowerCase() : null);
  };
  const insert = (p: HubPerson) => {
    const caret = ref.current?.selectionStart ?? body.length;
    const before = body.slice(0, caret).replace(/@([^\s@]*)$/, `@${p.name.split(" ")[0]} `);
    setBody(before + body.slice(caret));
    setMentions((m) => (m.some((x) => x.email === p.email) ? m : [...m, p]));
    setQuery(null);
    requestAnimationFrame(() => ref.current?.focus());
  };
  const send = async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    // Only mentions still written in the text are sent.
    const kept = mentions.filter((m) => text.includes(`@${m.name.split(" ")[0]}`)).map((m) => m.email);
    const ok = await onSubmit(text, kept);
    setBusy(false);
    if (ok) {
      setBody("");
      setMentions([]);
    }
  };
  return (
    <div className="relative flex-1 flex items-end gap-2 bg-muted/20 border border-border rounded-xl px-3 py-2 focus-within:border-primary transition-colors">
      <textarea
        ref={ref}
        rows={1}
        autoFocus={autoFocus}
        value={body}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (query !== null && matches.length && (e.key === "Enter" || e.key === "Tab")) {
            e.preventDefault();
            insert(matches[0]);
          } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void send();
          } else if (e.key === "Escape") {
            if (query !== null) setQuery(null);
            else onCancel?.();
          }
        }}
        aria-label={placeholder}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-transparent outline-none text-sm resize-none max-h-32 placeholder:text-muted-foreground/60"
        data-task-id={taskId}
      />
      <button
        type="button"
        onClick={() => void send()}
        disabled={!body.trim() || busy}
        aria-label={t("tasks.comments.send", { defaultValue: "Send" })}
        className="text-primary shrink-0 disabled:opacity-30"
      >
        <Send className="size-3.5" />
      </button>
      {query !== null && (
        <div className="absolute bottom-full start-0 mb-1 w-64 bg-background border border-border rounded-xl shadow-2xl p-1 z-50" role="listbox">
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t("tasks.hubs.newSquad.noMembersMatch", { defaultValue: "No members match" })}</p>
          ) : (
            matches.map((p) => (
              <button
                key={p.email}
                type="button"
                role="option"
                aria-selected={false}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(p)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted text-start"
              >
                <PersonAvatar name={p.name} size={22} className="border-0" />
                <span className="text-sm truncate">{p.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CommentBubble({
  c,
  me,
  replies,
  taskId,
  people,
  canReply,
  canAssign,
  onChanged,
  focusId,
}: {
  c: Comment;
  me: string;
  replies: Comment[];
  taskId: string;
  people: HubPerson[];
  canReply: boolean;
  /** Replies can't be replied to, but can be assigned (US 9.3). */
  canAssign?: boolean;
  onChanged: () => void;
  focusId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [menu, setMenu] = useState<false | "root" | "assign">(false);
  const mine = c.author_email === me;
  const mayAssign = canAssign ?? canReply;
  const failed = (err: unknown) => toast.error((err as TaskApiError)?.message || t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));
  const assign = (email: string | null) => {
    setMenu(false);
    taskApi.assignComment(c.id, email).then(() => {
      onChanged();
      toast.success(email ? t("tasks.inbox.commentAssigned", { defaultValue: "Comment assigned" }) : t("tasks.inbox.commentUnassigned", { defaultValue: "Comment unassigned" }));
    }, failed);
  };
  const resolve = (resolved: boolean) =>
    taskApi.resolveComment(c.id, resolved).then(() => {
      onChanged();
      toast.success(resolved ? t("tasks.inbox.resolvedToast", { defaultValue: "Marked as resolved" }) : t("tasks.inbox.unresolvedToast", { defaultValue: "Marked as unresolved" }));
    }, failed);
  // US 9.1 — opened from the Inbox at this comment.
  const focused = focusId === c.id || replies.some((r) => r.id === focusId);
  return (
    <li id={`task-comment-${c.id}`} className={cn("group/c rounded-xl transition-colors", focusId === c.id && "bg-primary/[0.07] ring-1 ring-primary/30 -mx-2 px-2 py-1")} data-focused={focused || undefined}>
      <div className="flex items-start gap-3">
        <PersonAvatar name={c.author} size={c.parent ? 24 : 28} className="border border-border shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="flex items-baseline gap-2 mb-1">
            <span className="text-xs font-bold">{c.author}</span>
            <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString(i18n.language, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            {c.edited && !c.deleted && <span className="text-[10px] text-muted-foreground -ms-2"> · {t("tasks.comments.edited", { defaultValue: "edited" })}</span>}
          </p>
          {c.deleted ? (
            <p className="text-sm text-muted-foreground italic bg-muted/30 rounded-xl px-3 py-2 w-fit">{t("tasks.comments.deleted", { defaultValue: "Comment deleted" })}</p>
          ) : editing ? (
            <div className="flex">
              <Composer
                taskId={taskId}
                people={people}
                autoFocus
                initial={c.body}
                placeholder={t("tasks.comments.edit", { defaultValue: "Edit" })}
                onCancel={() => setEditing(false)}
                onSubmit={async (body) => {
                  try {
                    await taskApi.editComment(c.id, body);
                    setEditing(false);
                    onChanged();
                    return true;
                  } catch {
                    toast.error(t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));
                    return false;
                  }
                }}
              />
            </div>
          ) : (
            <div className="text-sm bg-muted/30 rounded-xl px-3 py-2 leading-relaxed text-foreground w-fit max-w-full">
              <Markdown text={c.body} className="text-sm" />
            </div>
          )}
          {c.assignee && !c.deleted && (
            <p className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
              <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-primary/10 font-semibold">
                <UserPlus className="size-3" /> {t("tasks.inbox.assignedTo", { name: c.assignee.name, defaultValue: "Assigned to {{name}}" })}
              </span>
              {c.resolved ? (
                <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-green-500/10 text-green-700 dark:text-green-300 font-semibold">
                  <Check className="size-3" /> {t("tasks.inbox.resolved", { defaultValue: "Resolved" })}
                </span>
              ) : null}
              <button type="button" onClick={() => void resolve(!c.resolved)} className="font-semibold text-muted-foreground hover:text-foreground">
                {c.resolved ? t("tasks.inbox.unresolve", { defaultValue: "Mark as unresolved" }) : t("tasks.inbox.resolve", { defaultValue: "Resolve" })}
              </button>
            </p>
          )}
        </div>
        {!c.deleted && !editing && ((canReply && !c.parent) || mine || mayAssign) && (
          <span className={cn("relative flex items-center gap-0.5 pt-5 shrink-0 transition-opacity focus-within:opacity-100", menu ? "opacity-100" : "opacity-0 group-hover/c:opacity-100")}>
            {canReply && !c.parent && (
              <button type="button" onClick={() => setReplying((r) => !r)} title={t("tasks.comments.reply", { defaultValue: "Reply" })} aria-label={t("tasks.comments.reply", { defaultValue: "Reply" })} className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
                <Reply className="size-3" />
              </button>
            )}
            {(mine || mayAssign) && (
            <button type="button" onClick={() => setMenu((m) => (m ? false : "root"))} title={t("tasks.comments.actions", { defaultValue: "Comment actions" })} aria-label={t("tasks.comments.actions", { defaultValue: "Comment actions" })} aria-haspopup="menu" className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
              <MoreHorizontal className="size-3" />
            </button>
            )}
            <Dropdown open={!!menu} onClose={() => setMenu(false)} align="end" className="w-56 p-1 max-h-[60vh] overflow-y-auto">
              <div role="menu" aria-label={t("tasks.comments.actions", { defaultValue: "Comment actions" })}>
                {menu === "assign" ? (
                  <>
                    <button type="button" onClick={() => setMenu("root")} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
                      <ChevronLeft className="size-3 rtl:rotate-180" /> {t("tasks.inbox.assignTo", { defaultValue: "Assign to…" })}
                    </button>
                    {people.map((p) => (
                      <button key={p.email} type="button" role="menuitem" onClick={() => assign(p.email)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg text-start">
                        <PersonAvatar name={p.name} size={20} className="border-0" />
                        <span className="flex-1 truncate">{p.name}</span>
                        {c.assignee?.email === p.email && <Check className="size-3.5 text-primary" />}
                      </button>
                    ))}
                    {c.assignee && (
                      <button type="button" role="menuitem" onClick={() => assign(null)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg text-muted-foreground">
                        <X className="size-3.5" /> {t("tasks.inbox.unassign", { defaultValue: "Remove assignee" })}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {mayAssign && (
                      <button type="button" role="menuitem" onClick={() => setMenu("assign")} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg">
                        <UserPlus className="size-3.5" /> {t("tasks.inbox.assignTo", { defaultValue: "Assign to…" })}
                      </button>
                    )}
                    {c.assignee && (
                      <button type="button" role="menuitem" onClick={() => { setMenu(false); void resolve(!c.resolved); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg">
                        <CircleCheck className="size-3.5" /> {c.resolved ? t("tasks.inbox.unresolve", { defaultValue: "Mark as unresolved" }) : t("tasks.inbox.resolve", { defaultValue: "Resolve" })}
                      </button>
                    )}
                    {mine && (
                      <>
                        <button type="button" role="menuitem" onClick={() => { setMenu(false); setEditing(true); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted rounded-lg">
                          <Pencil className="size-3.5" /> {t("tasks.comments.edit", { defaultValue: "Edit" })}
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setMenu(false); setConfirm(true); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 rounded-lg">
                          <Trash2 className="size-3.5" /> {t("tasks.comments.delete", { defaultValue: "Delete" })}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </Dropdown>
          </span>
        )}
      </div>
      {(replies.length > 0 || replying) && (
        <ul className="ms-10 mt-2 space-y-2 border-s border-border ps-4">
          {replies.map((r) => (
            <CommentBubble key={r.id} c={r} me={me} replies={[]} taskId={taskId} people={people} canReply={false} canAssign={canReply} onChanged={onChanged} focusId={focusId} />
          ))}
          {replying && (
            <li className="flex">
              <Composer
                taskId={taskId}
                people={people}
                autoFocus
                placeholder={t("tasks.comments.replyPlaceholder", { defaultValue: "Write a reply…" })}
                onCancel={() => setReplying(false)}
                onSubmit={async (body, mentions) => {
                  try {
                    await taskApi.addComment(taskId, body, mentions, c.id);
                    setReplying(false);
                    onChanged();
                    return true;
                  } catch {
                    toast.error(t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));
                    return false;
                  }
                }}
              />
            </li>
          )}
        </ul>
      )}
      {confirm && (
        <ConfirmDialog
          title={t("tasks.comments.deleteTitle", { defaultValue: "Delete this comment?" })}
          body={t("tasks.comments.deleteBody", { defaultValue: "It's removed for everyone, including from notifications." })}
          confirmLabel={t("tasks.comments.delete", { defaultValue: "Delete" })}
          onClose={() => setConfirm(false)}
          onConfirm={() =>
            void taskApi
              .removeComment(c.id)
              .then(onChanged)
              .catch(() => toast.error(t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." })))
          }
        />
      )}
    </li>
  );
}

/** One activity line, in words — "Aya Ayman moved this from In progress to Review · 2h" (US 4.8). */
export function useActivityText() {
  const { t, i18n } = useTranslation();
  const status = (s: string) =>
    ({
      todo: t("tasks.hubs.status.todo", { defaultValue: "To do" }),
      in_progress: t("tasks.hubs.status.inProgress", { defaultValue: "In progress" }),
      review: t("tasks.hubs.status.review", { defaultValue: "Review" }),
      done: t("tasks.hubs.status.done", { defaultValue: "Done" }),
      pending: t("tasks.hubs.status.todo", { defaultValue: "To do" }),
      completed: t("tasks.hubs.status.done", { defaultValue: "Done" }),
    })[s] ?? s;
  const date = (v: string) => (v ? new Date(`${v}T00:00:00`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" }) : t("tasks.dates.noDate", { defaultValue: "No date" }));
  const value = (field: string, v: string) => {
    if (!v) return "—";
    if (field === "board_status" || field === "status") return status(v);
    if (field === "due_date" || field === "start_date") return date(v);
    if (field === "priority") return t(`tasks.priority.${v}`, { defaultValue: v });
    if (field === "category") return t(`tasks.category.${v}`, { defaultValue: v });
    if (field === "task_type") return t(`tasks.type.${v}`, { defaultValue: v });
    if (field === "repeat_rule") return v;
    return v;
  };
  return (a: ActivityRow): string => {
    const who = a.actor || t("tasks.activity.someone", { defaultValue: "Someone" });
    let text: string;
    switch (a.kind) {
      case "created":
        text = t("tasks.activity.createdBy", { defaultValue: "Created by {{who}}", who });
        break;
      case "archived":
      case "unarchived":
      case "deleted":
      case "restored":
        text = t(`tasks.activity.${a.kind}`, { defaultValue: `{{who}} ${a.kind} this`, who });
        break;
      case "label_added":
        text = t("tasks.activity.labelAdded", { defaultValue: "{{who}} added the label {{name}}", who, name: a.new });
        break;
      case "label_removed":
        text = t("tasks.activity.labelRemoved", { defaultValue: "{{who}} removed the label {{name}}", who, name: a.old });
        break;
      case "assignee_added":
        text = t("tasks.activity.assigneeAdded", { defaultValue: "{{who}} assigned {{name}}", who, name: a.new });
        break;
      case "assignee_removed":
        text = t("tasks.activity.assigneeRemoved", { defaultValue: "{{who}} unassigned {{name}}", who, name: a.old });
        break;
      case "subtask_added":
      case "subtask_done":
      case "subtask_reopened":
      case "subtask_removed":
        text = t(`tasks.activity.${a.kind}`, { defaultValue: `{{who}} ${a.kind.replace("subtask_", "")} subtask {{name}}`, who, name: a.new || a.old });
        break;
      case "attachment_added":
        text = t("tasks.activity.attachmentAdded", { defaultValue: "{{who}} attached {{name}}", who, name: a.new });
        break;
      case "attachment_removed":
        text = t("tasks.activity.attachmentRemoved", { defaultValue: "{{who}} deleted the file {{name}}", who, name: a.old });
        break;
      case "reminder_set":
        text = t("tasks.activity.reminderSet", { defaultValue: "{{who}} set a reminder", who });
        break;
      default:
        if (a.field === "board_status" || a.field === "status")
          text = t("tasks.activity.moved", { defaultValue: "{{who}} moved this from {{from}} to {{to}}", who, from: value(a.field, a.old), to: value(a.field, a.new) });
        else if (a.field === "description") text = t("tasks.activity.description", { defaultValue: "{{who}} updated the description", who });
        else if (a.field === "repeat_rule")
          text = a.new
            ? t("tasks.activity.repeatSet", { defaultValue: "{{who}} set it to repeat: {{rule}}", who, rule: a.new })
            : t("tasks.activity.repeatStopped", { defaultValue: "{{who}} stopped the repeat", who });
        else if (a.field === "squad" || a.field === "hub") text = t("tasks.activity.movedSquad", { defaultValue: "{{who}} moved this to another squad", who });
        else if (a.field === "personal_list") text = t("tasks.activity.movedList", { defaultValue: "{{who}} moved this to another list", who });
        else
          text = t("tasks.activity.changed", {
            defaultValue: "{{who}} changed {{field}} from {{from}} to {{to}}",
            who,
            field: t(`tasks.activity.field.${a.field}`, { defaultValue: a.field.replace("_", " ") }),
            from: value(a.field, a.old),
            to: value(a.field, a.new),
          });
    }
    return a.via === "mochi" ? `${text} · ${t("tasks.activity.viaMochi", { defaultValue: "via Mochi" })}` : text;
  };
}

/** Same icon vocabulary as the design's activity feed. */
function activityIcon(a: ActivityRow): LucideIcon {
  switch (a.kind) {
    case "created": return CirclePlus;
    case "archived": return Archive;
    case "unarchived": case "restored": return ArchiveRestore;
    case "deleted": return Trash2;
    case "label_added": case "label_removed": return Tag;
    case "assignee_added": case "assignee_removed": return Users;
    case "subtask_added": return ListPlus;
    case "subtask_done": case "subtask_reopened": case "subtask_removed": return ListChecks;
    case "attachment_added": case "attachment_removed": return Paperclip;
    case "reminder_set": return Bell;
  }
  if (a.field === "board_status" || a.field === "status") return CircleDot;
  if (a.field === "priority") return Flag;
  if (a.field === "due_date" || a.field === "start_date") return CalendarDays;
  if (a.field === "title") return Pencil;
  if (a.field === "description") return FileText;
  if (a.field === "repeat_rule") return Repeat;
  if (a.field === "squad" || a.field === "hub" || a.field === "personal_list") return FolderInput;
  return CircleDot;
}

const ago = (iso: string, locale: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  if (s < 60) return rtf.format(0, "second");
  if (s < 3600) return rtf.format(-Math.round(s / 60), "minute");
  if (s < 86400) return rtf.format(-Math.round(s / 3600), "hour");
  return rtf.format(-Math.round(s / 86400), "day");
};

export function DiscussionSection({
  task,
  me,
  people,
  onChanged,
  focusCommentId,
}: {
  task: TaskFull;
  me: string;
  people: HubPerson[];
  onChanged: () => void;
  /** US 9.1 — open at this comment (from the Inbox). */
  focusCommentId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<"comments" | "all">("comments");
  const comments = useComments(task.id);
  const loaded = !!comments.data;
  useEffect(() => {
    if (!focusCommentId || !loaded) return;
    setView("comments");
    const id = window.setTimeout(() => document.getElementById(`task-comment-${focusCommentId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    return () => window.clearTimeout(id);
  }, [focusCommentId, loaded]);
  const activity = useActivity(task.id);
  const text = useActivityText();
  const list = comments.data ?? [];
  const top = list.filter((c) => !c.parent);
  const repliesOf = (id: string) => list.filter((c) => c.parent === id);
  const refresh = () => {
    void comments.refetch();
    void activity.refetch();
    onChanged();
  };
  const merged = useMemo(() => {
    const rows: ({ kind: "comment"; at: string; c: Comment } | { kind: "activity"; at: string; a: ActivityRow })[] = [
      ...top.map((c) => ({ kind: "comment" as const, at: c.created_at, c })),
      ...(activity.data ?? []).map((a) => ({ kind: "activity" as const, at: a.at, a })),
    ];
    return rows.sort((x, y) => (x.at < y.at ? 1 : -1));
  }, [top, activity.data]);
  const count = list.filter((c) => !c.deleted).length;

  return (
    <section className="px-5 py-4" aria-label={t("tasks.comments.title", { defaultValue: "Comments" })}>
      <div className="flex items-center gap-4 mb-4 border-b border-border/50" role="tablist">
        {(
          [
            ["comments", t("tasks.comments.title", { defaultValue: "Comments" }) + (count ? ` (${count})` : "")],
            ["all", t("tasks.comments.allActivity", { defaultValue: "All activity" })],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
            className={cn("text-xs font-bold pb-3 border-b-2 -mb-px transition-colors", view === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
          >
            {label}
          </button>
        ))}
      </div>
      {/* US 13.9 — Mochi's summary of a long thread. */}
      {view === "comments" && <ThreadSummary taskId={task.id} commentCount={count} onPosted={refresh} />}
      {view === "comments" ? (
        top.length === 0 ? (
          <p className="text-xs text-muted-foreground/50 italic text-center py-4">{t("tasks.comments.empty", { defaultValue: "No comments yet — be the first to say something." })}</p>
        ) : (
          <ul className="space-y-4">
            {top.map((c) => (
              <CommentBubble key={c.id} c={c} me={me} replies={repliesOf(c.id)} taskId={task.id} people={people} canReply={task.can_edit} onChanged={refresh} focusId={focusCommentId} />
            ))}
          </ul>
        )
      ) : (
        <ul className="space-y-3" data-testid="activity-list">
          {merged.map((row) =>
            row.kind === "comment" ? (
              <CommentBubble key={`c${row.c.id}`} c={row.c} me={me} replies={repliesOf(row.c.id)} taskId={task.id} people={people} canReply={task.can_edit} onChanged={refresh} />
            ) : (
              <li key={`a${row.a.id}`} className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
                {(() => {
                  const Icon = activityIcon(row.a);
                  return <Icon className="size-[13px] text-muted-foreground/70 shrink-0" aria-hidden />;
                })()}
                <span className="flex-1">{text(row.a)}</span>
                <span className="text-[10px] shrink-0">{ago(row.a.at, i18n.language)}</span>
              </li>
            ),
          )}
        </ul>
      )}
      {task.can_edit && (
        <div className="flex items-end gap-2 mt-4 -mx-5 px-4 py-3 border-t border-border sticky bottom-0 bg-background/95">
          <PersonAvatar name={people.find((p) => p.email === me)?.name || me} size={28} className="border border-border shrink-0 mb-1" />
          <Composer
            taskId={task.id}
            people={people}
            placeholder={t("tasks.comments.placeholderMention", { defaultValue: "Leave a comment…  @ to mention" })}
            onSubmit={async (body, mentions) => {
              try {
                await taskApi.addComment(task.id, body, mentions);
                setView("comments");
                refresh();
                return true;
              } catch {
                toast.error(t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." }));
                return false;
              }
            }}
          />
        </div>
      )}
    </section>
  );
}
